import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { ArrowRight, Loader2, Lock, ShieldCheck } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { ensurePixelReady, trackEvent as trackMetaEvent } from "../lib/metaPixel";

/**
 * V232 — Inline Snapshot Widget.
 *
 * Reusable compact 2-field form (domain + email) that fires the SAME
 * `/payments/checkout` call as the full `/snapshot` landing page.
 * Designed to be dropped into:
 *   - /compare/* hero (reduces 1 navigation hop → +15-25% conversion
 *     per Baymard comparison-page cohort studies)
 *   - blog post sidebars
 *   - city/firm programmatic pages (future)
 *
 * All conversions are tagged with the `source` prop so GA4 / Meta
 * attribution shows widget placement in the funnel.
 *
 * Props:
 *   source     string   REQUIRED. Attribution tag, e.g. "compare_hero_widget".
 *   variant    "dark" | "light"   default "dark"
 *   headline   string?  Optional override above the fields
 *   subcopy    string?  Optional override below the headline
 *   ctaLabel   string?  Default "Spustiť audit za €7"
 *   defaultUrl string?  Prefill domain field (e.g. from a URL query)
 */
export default function InlineSnapshotWidget({
  source,
  variant = "dark",
  headline,
  subcopy,
  ctaLabel = "Spustiť audit za €7",
  defaultUrl = "",
}) {
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState(defaultUrl);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isDark = variant === "dark";

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !url.trim()) return;
    setSubmitting(true);
    setError(null);

    const payload = {
      value: 7,
      currency: "EUR",
      item_id: "snapshot_tripwire",
      source,
    };
    trackEvent("start_checkout", payload);

    // Meta Pixel + CAPI (fire-and-forget, don't block checkout on tracking)
    ensurePixelReady().then((ready) => {
      if (ready) {
        trackMetaEvent("InitiateCheckout", {
          value: 7,
          currency: "EUR",
          content_ids: ["snapshot_tripwire"],
          content_type: "product",
          email: email.trim(),
        }).catch(() => {});
      }
    });

    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "snapshot_tripwire",
        origin_url: window.location.origin,
        success_url: `/fix-my-seo?email=${encodeURIComponent(email)}&url=${encodeURIComponent(url)}`,
        cancel_url: window.location.pathname,
        email: email.trim(),
        language: "sk",
        metadata: { source, target_url: url.trim() },
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      setError("Checkout URL missing — skús to prosím znova.");
    } catch (err) {
      console.error("inline-snapshot checkout failed", err);
      setError("Niečo sa pokazilo. Skús to prosím o chvíľu znova.");
    }
    setSubmitting(false);
  };

  const wrapperCls = isDark
    ? "bg-white/5 border border-white/10 backdrop-blur-sm"
    : "bg-white border border-slate-200";
  const inputCls = isDark
    ? "w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:border-[#a855f7] focus:outline-none"
    : "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#a855f7] focus:outline-none";
  const labelCls = isDark
    ? "block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 font-['JetBrains_Mono']"
    : "block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 font-['JetBrains_Mono']";
  const headlineCls = isDark ? "text-white" : "text-slate-900";
  const subcopyCls = isDark ? "text-gray-400" : "text-slate-600";
  const trustCls = isDark ? "text-gray-500" : "text-slate-500";

  const defaultHeadline = (
    <>
      Spusti si <span className="text-[#a855f7]">audit vlastnej domény</span> za €7
    </>
  );

  return (
    <form
      onSubmit={handleSubmit}
      className={`rounded-2xl p-5 sm:p-6 ${wrapperCls}`}
      data-testid={`inline-snapshot-widget-${source}`}
    >
      <h3 className={`text-xl sm:text-2xl font-black font-['Cabinet_Grotesk'] mb-2 leading-tight ${headlineCls}`} data-testid="inline-widget-headline">
        {headline || defaultHeadline}
      </h3>
      <p className={`text-sm mb-5 leading-relaxed ${subcopyCls}`} data-testid="inline-widget-subcopy">
        {subcopy || "Report za 10 minút. Žiadne čakanie, žiadna registrácia pred platbou. 14-dňová záruka vrátenia peňazí."}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className={labelCls}>Vaša doména</label>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="stripe.com"
            className={inputCls}
            data-testid="inline-widget-url"
            disabled={submitting}
          />
        </div>
        <div>
          <label className={labelCls}>Email na doručenie</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vy@firma.sk"
            className={inputCls}
            data-testid="inline-widget-email"
            disabled={submitting}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting || !email.trim() || !url.trim()}
        data-testid="inline-widget-submit"
        className="w-full bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-3.5 rounded-lg flex items-center justify-center gap-2 text-base transition-all"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            {ctaLabel} <ArrowRight className="w-5 h-5" />
          </>
        )}
      </button>

      {error && (
        <p className="text-xs text-red-400 mt-3" role="alert" data-testid="inline-widget-error">
          {error}
        </p>
      )}

      <div className={`flex items-center justify-center gap-3 flex-wrap mt-4 pt-4 border-t ${isDark ? "border-white/5" : "border-slate-200"} text-[10px] font-bold uppercase tracking-wider ${trustCls}`} data-testid="inline-widget-trust">
        <span className="inline-flex items-center gap-1">
          <Lock className="w-3 h-3" />
          Stripe secure
        </span>
        <span className="opacity-30">·</span>
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" />
          14-dní garancia
        </span>
        <span className="opacity-30">·</span>
        <span>🇸🇰 EU-hosting</span>
      </div>
    </form>
  );
}
